import { useMemo, useState } from 'react';
import type { UsageRow } from '@/api/client';
import { fmtMinutes, fmtNum, fmtUsdExact } from '@/lib/format';
import { Card, SectionHeader, cell, cx, headCell, input, mutedText, numCell, row } from '@/ui';

type SortKey = 'revenue' | 'messages' | 'offers' | 'name' | 'response';

function nameOf(u: UsageRow): string {
  return u.fullName?.trim() || u.email;
}

function sum(rows: UsageRow[], pick: (u: UsageRow) => number): number {
  return rows.reduce((acc, u) => acc + (pick(u) || 0), 0);
}

export function UsageTable({
  rows,
  rangeLabel,
  selectedCount,
}: {
  rows: UsageRow[];
  rangeLabel: string;
  selectedCount: number;
}) {
  const [sort, setSort] = useState<SortKey>('revenue');

  const sorted = useMemo(() => {
    const out = rows.slice();
    out.sort((a, b) => {
      if (sort === 'messages')
        return b.autopilotMessages + b.dashboardMessages - (a.autopilotMessages + a.dashboardMessages);
      if (sort === 'offers') return b.offersSent - a.offersSent;
      if (sort === 'name') return nameOf(a).localeCompare(nameOf(b));
      if (sort === 'response') {
        // Locations with no measured response time sort last, not first.
        const av = a.avgResponseTimeMinutes ?? Number.POSITIVE_INFINITY;
        const bv = b.avgResponseTimeMinutes ?? Number.POSITIVE_INFINITY;
        return av - bv;
      }
      return b.revenueTotalUsdCents - a.revenueTotalUsdCents;
    });
    return out;
  }, [rows, sort]);

  const totals = useMemo(() => {
    const samples = sum(rows, (u) => u.responseSampleSize);
    return {
      autopilot: sum(rows, (u) => u.autopilotMessages),
      dashboard: sum(rows, (u) => u.dashboardMessages),
      offers: sum(rows, (u) => u.offersSent),
      sold: sum(rows, (u) => u.upsellsSold),
      eci: sum(rows, (u) => u.revenueEarlyCheckinUsdCents),
      lco: sum(rows, (u) => u.revenueLateCheckoutUsdCents),
      extraNights: sum(rows, (u) => u.revenueExtraNightsUsdCents),
      custom: sum(rows, (u) => u.revenueCustomUsdCents),
      other: sum(rows, (u) => u.revenueOtherUsdCents),
      total: sum(rows, (u) => u.revenueTotalUsdCents),
      // Sample-weighted, so a location with 4 measured replies cannot swing the
      // benchmark as hard as one with 4,000.
      response: samples ? sum(rows, (u) => (u.avgResponseTimeMinutes ?? 0) * u.responseSampleSize) / samples : null,
    };
  }, [rows]);

  return (
    <Card>
      <SectionHeader
        title="Usage and upsell revenue"
        subtitle={`Messaging, offers and upsell revenue by location. ${rangeLabel}`}
        right={
          <select className={cx(input, 'pr-8')} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="revenue">Sort: upsell revenue</option>
            <option value="messages">Sort: messages</option>
            <option value="offers">Sort: offers sent</option>
            <option value="response">Sort: fastest response</option>
            <option value="name">Sort: name</option>
          </select>
        }
      />
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={headCell}>Location</th>
              <th className={cx(headCell, 'text-right')}>Autopilot</th>
              <th className={cx(headCell, 'text-right')}>Dashboard</th>
              <th className={cx(headCell, 'text-right')}>Offers sent</th>
              <th className={cx(headCell, 'text-right')}>Upsells sold</th>
              <th className={cx(headCell, 'text-right')}>ECI</th>
              <th className={cx(headCell, 'text-right')}>LCO</th>
              <th className={cx(headCell, 'text-right')}>Gap and extra nights</th>
              <th className={cx(headCell, 'text-right')}>Custom</th>
              <th className={cx(headCell, 'text-right')}>Other</th>
              <th className={cx(headCell, 'text-right')}>Total revenue</th>
              <th className={cx(headCell, 'text-right')}>Avg response</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.userId} className={row}>
                <td className={cell}>
                  <div className="font-medium text-neutral-900">{nameOf(u)}</div>
                  <div className="text-xs text-neutral-500">#{u.userId}</div>
                </td>
                <td className={numCell}>{fmtNum(u.autopilotMessages)}</td>
                <td className={numCell}>{fmtNum(u.dashboardMessages)}</td>
                <td className={numCell}>{fmtNum(u.offersSent)}</td>
                <td className={numCell}>{fmtNum(u.upsellsSold)}</td>
                <td className={numCell}>{fmtUsdExact(u.revenueEarlyCheckinUsdCents)}</td>
                <td className={numCell}>{fmtUsdExact(u.revenueLateCheckoutUsdCents)}</td>
                <td className={numCell}>{fmtUsdExact(u.revenueExtraNightsUsdCents)}</td>
                <td className={numCell}>{fmtUsdExact(u.revenueCustomUsdCents)}</td>
                <td className={numCell} title="Any other upsell type">
                  {fmtUsdExact(u.revenueOtherUsdCents)}
                </td>
                <td className={cx(numCell, 'font-semibold text-neutral-900')}>{fmtUsdExact(u.revenueTotalUsdCents)}</td>
                <td className={numCell}>{fmtMinutes(u.avgResponseTimeMinutes)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className={cx(cell, mutedText)} colSpan={12}>
                  No usage in this date range.
                </td>
              </tr>
            )}
          </tbody>
          {sorted.length > 0 && (
            <tfoot className="sticky bottom-0 bg-neutral-50">
              <tr className="border-t border-neutral-200">
                <td className={cx(cell, 'font-semibold')}>
                  {selectedCount > 0 ? 'Selected locations' : 'All locations'}
                </td>
                <td className={cx(numCell, 'font-semibold')}>{fmtNum(totals.autopilot)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtNum(totals.dashboard)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtNum(totals.offers)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtNum(totals.sold)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.eci)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.lco)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.extraNights)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.custom)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.other)}</td>
                <td className={cx(numCell, 'font-semibold')}>{fmtUsdExact(totals.total)}</td>
                <td className={cx(numCell, 'font-semibold')} title="Sample-weighted average across locations">
                  {fmtMinutes(totals.response)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}
